import React, { useState } from "react";
import { 
    Card, Row, Col, Form, Button, Table, Badge, ProgressBar, Alert, Spinner
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";
import * as XLSX from 'xlsx';


export default function ImportProduits() {
    const navigate = useNavigate();
    const [importStep, setImportStep] = useState(1);
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState([]);
    const [validationResults, setValidationResults] = useState({});
    const [importProgress, setImportProgress] = useState(0); 
    const [importStatus, setImportStatus] = useState('idle');
    const [importStats, setImportStats] = useState({});
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState(null);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const expectedColumns = [
        { key: 'reference', label: 'Référence', required: false, example: 'REF2024001' },
        { key: 'designation', label: 'Désignation', required: true, example: 'Produit test' },
        { key: 'description', label: 'Description', required: false, example: 'Description du produit' },
        { key: 'prix_achat_ht', label: 'Prix achat HT', required: true, example: '1000' },
        { key: 'prix_vente_ht', label: 'Prix vente HT', required: true, example: '1500' },
        { key: 'seuil_alerte', label: 'Seuil alerte', required: false, example: '5' },
        { key: 'stock_initial', label: 'Stock initial', required: false, example: '10' }
    ];

    const downloadTemplate = () => {
        const data = [
            ['reference', 'designation', 'description', 'prix_achat_ht', 'prix_vente_ht', 'seuil_alerte', 'stock_initial'],
            ['REF001', 'Produit exemple 1', 'Description produit 1', '1000', '1500', '5', '10'],
            ['REF002', 'Produit exemple 2', 'Description produit 2', '2000', '2500', '3', '5'],
        ];

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Produits");
        XLSX.writeFile(workbook, "modele-import-produits.xlsx");
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedExtensions = ['.csv', '.xls', '.xlsx'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        
        if (!allowedExtensions.includes(fileExtension)) {
            alert('Veuillez sélectionner un fichier CSV ou Excel valide');
            return;
        }

        setSelectedFile(file);
        previewFile(file);
    };

    const previewFile = (file) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                let previewData = [];
                
                if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
                    const content = e.target.result;
                    const lines = content.split('\n').slice(0, 11);
                    previewData = lines.map(line => 
                        line.split(',').map(cell => cell.trim().replace(/^"|"$/g, ''))
                    );
                } else {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    previewData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }).slice(0, 11);
                }
                
                setFilePreview(previewData);
                validateFile(previewData);
                
            } catch (error) {
                console.error('Erreur lecture fichier:', error);
                alert('Erreur lors de la lecture du fichier');
            }
        };

        if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
            reader.readAsText(file, 'UTF-8');
        } else {
            reader.readAsArrayBuffer(file);
        }
    };

    const validateFile = (previewData) => {
        const results = {
            valid: 0,
            invalid: 0,
            errors: [],
            columnMatch: true,
            produits: []
        };

        if (previewData.length > 0) {
            const headers = previewData[0].map(h => h?.toString().toLowerCase().trim());
            
            const requiredHeaders = ['designation', 'prix_achat_ht', 'prix_vente_ht'];
            
            requiredHeaders.forEach(required => {
                if (!headers.includes(required)) {
                    results.columnMatch = false;
                    results.errors.push(`Colonne '${required}' manquante`);
                }
            });

            const colIndex = {
                reference: headers.indexOf('reference'),
                designation: headers.indexOf('designation'),
                description: headers.indexOf('description'),
                prix_achat_ht: headers.indexOf('prix_achat_ht'),
                prix_vente_ht: headers.indexOf('prix_vente_ht'),
                seuil_alerte: headers.indexOf('seuil_alerte'),
                stock_initial: headers.indexOf('stock_initial')
            };

            if (results.columnMatch) {
                previewData.slice(1).forEach((row, index) => {
                    const ligne = index + 2;
                    
                    if (!row[colIndex.designation] || !row[colIndex.prix_achat_ht] || !row[colIndex.prix_vente_ht]) {
                        results.invalid++;
                        results.errors.push(`Ligne ${ligne}: Champs obligatoires manquants`);
                    } 
                    else if (isNaN(parseFloat(row[colIndex.prix_achat_ht])) || parseFloat(row[colIndex.prix_achat_ht]) <= 0) {
                        results.invalid++;
                        results.errors.push(`Ligne ${ligne}: Prix achat invalide`);
                    }
                    else if (isNaN(parseFloat(row[colIndex.prix_vente_ht])) || parseFloat(row[colIndex.prix_vente_ht]) <= 0) {
                        results.invalid++;
                        results.errors.push(`Ligne ${ligne}: Prix vente invalide`);
                    }
                    else {
                        const produit = {
                            reference: colIndex.reference !== -1 ? row[colIndex.reference]?.toString().trim() || '' : '',
                            designation: row[colIndex.designation]?.toString().trim(),
                            description: colIndex.description !== -1 ? row[colIndex.description]?.toString().trim() || '' : '',
                            prix_achat_ht: parseFloat(row[colIndex.prix_achat_ht]),
                            prix_vente_ht: parseFloat(row[colIndex.prix_vente_ht]),
                            seuil_alerte: colIndex.seuil_alerte !== -1 ? parseInt(row[colIndex.seuil_alerte]) || 5 : 5,
                            stock_initial: colIndex.stock_initial !== -1 ? parseInt(row[colIndex.stock_initial]) || 0 : 0
                        };
                        
                        results.produits.push(produit);
                        results.valid++;
                    }
                });
            }
        }

        setValidationResults(results);
        setImportStep(2);
    };

    const importProduits = async () => {
        setImporting(true);
        setImportStatus('importing');
        setImportProgress(0);
        setError(null);

        try {
            const token = localStorage.getItem("token");
            
            const progressInterval = setInterval(() => {
                setImportProgress(prev => Math.min(prev + 10, 90));
            }, 300);

            const results = {
                total: validationResults.produits.length,
                imported: 0,
                failed: 0,
                errors: []
            };

            for (let i = 0; i < validationResults.produits.length; i++) {
                const produit = validationResults.produits[i];
                
                try {
                    const response = await fetch(
                        `${API_BASE_URL}/produits.php`,
                        {
                            method: 'POST',
                            headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(produit)
                        }
                    );

                    const result = await response.json();

                    if (result.success) {
                        results.imported++;
                    } else {
                        results.failed++;
                        results.errors.push(`Produit ${produit.designation}: ${result.message || 'Erreur inconnue'}`);
                    }
                } catch (err) {
                    results.failed++;
                    results.errors.push(`Produit ${produit.designation}: Erreur de connexion`);
                }

                setImportProgress(Math.floor(((i + 1) / validationResults.produits.length) * 100));
            }

            clearInterval(progressInterval);
            setImportProgress(100);
            
            setImportStats(results);
            setImportStatus(results.failed === 0 ? 'success' : results.imported > 0 ? 'partial' : 'error');
            
            if (results.failed === 0) {
                setSuccessMessage(`${results.imported} produits importés avec succès !`);
                setShowSuccessAlert(true);
            }

        } catch (error) {
            console.error("Erreur import:", error);
            setImportStatus('error');
            setError(error.message || "Erreur lors de l'import");
        } finally {
            setImporting(false);
            setImportStep(3);
        }
    };

    const getProgressVariant = () => {
        if (importStatus === 'success') return 'success';
        if (importStatus === 'error') return 'danger';
        return 'primary';
    };

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-upload-cloud-2-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Importer des produits</h4>
                                    <p className="text-muted small mb-0">Importez vos produits depuis un fichier Excel ou CSV</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-primary" 
                                onClick={downloadTemplate}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                            >
                                <i className="ri-download-line"></i>
                                Télécharger le modèle
                            </Button>
                        </div>
                    </div>

                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}

                    {/* Étapes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-4">
                            <div className="steps-container">
                                <div className={`step-item ${importStep >= 1 ? 'active' : ''}`}>
                                    <div className="step-circle">1</div>
                                    <div className="step-label">Fichier</div>
                                </div>
                                <div className="step-line"></div>
                                <div className={`step-item ${importStep >= 2 ? 'active' : ''}`}>
                                    <div className="step-circle">2</div>
                                    <div className="step-label">Validation</div>
                                </div>
                                <div className="step-line"></div>
                                <div className={`step-item ${importStep >= 3 ? 'active' : ''}`}>
                                    <div className="step-circle">3</div>
                                    <div className="step-label">Résultats</div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Étape 1: Upload */}
                    {importStep === 1 && (
                        <Card className="border-0 shadow-sm rounded-3">
                            <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                <h5 className="mb-0 text-white">
                                    <i className="ri-file-upload-line me-2"></i>
                                    Sélectionner le fichier
                                </h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <div className="upload-area text-center p-5">
                                    <i className="ri-file-upload-line display-1 text-primary mb-3" style={{ color: '#21ACCD' }}></i>
                                    <h5>Glissez-déposez votre fichier</h5>
                                    <p className="text-muted mb-4">
                                        Formats supportés: CSV, Excel (.xlsx, .xls)
                                    </p>
                                    <Form.Group>
                                        <Form.Control
                                            type="file"
                                            accept=".csv,.xlsx,.xls"
                                            onChange={handleFileSelect}
                                            className="d-none"
                                            id="fileInput"
                                        />
                                        <Button 
                                            variant="primary"
                                            onClick={() => document.getElementById('fileInput').click()}
                                            className="rounded-pill px-4"
                                            style={{ background: '#21ACCD', border: 'none' }}
                                        >
                                            <i className="ri-folder-open-line me-2"></i>
                                            Parcourir
                                        </Button>
                                    </Form.Group>
                                </div>

                                <div className="mt-4">
                                    <h6 className="fw-semibold mb-3" style={{ color: '#21ACCD' }}>
                                        <i className="ri-information-line me-2"></i>
                                        Format attendu
                                    </h6>
                                    <div className="table-responsive">
                                        <Table striped bordered className="align-middle small">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>Colonne</th>
                                                    <th>Obligatoire</th>
                                                    <th>Exemple</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {expectedColumns.map(col => (
                                                    <tr key={col.key}>
                                                        <td className="fw-semibold">{col.label}</td>
                                                        <td>
                                                            {col.required ? 
                                                                <Badge className="rounded-pill px-2" style={{ background: '#ef4444' }}>Obligatoire</Badge> : 
                                                                <Badge className="rounded-pill px-2" style={{ background: '#64748b' }}>Optionnel</Badge>
                                                            }
                                                        </td>
                                                        <td><code>{col.example}</code></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Étape 2: Validation */}
                    {importStep === 2 && (
                        <Card className="border-0 shadow-sm rounded-3">
                            <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                <h5 className="mb-0 text-white">
                                    <i className="ri-checkbox-circle-line me-2"></i>
                                    Validation des données
                                </h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {importing ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                            <span className="visually-hidden">Chargement...</span>
                                        </div>
                                        <h5 className="mb-3">Import en cours...</h5>
                                        <div className="progress-bar-custom" style={{ width: '100%', height: '8px', marginBottom: '1rem' }}>
                                            <div className="progress-fill" style={{ width: `${importProgress}%`, background: '#21ACCD' }}></div>
                                        </div>
                                        <p className="text-muted">
                                            Traitement de {validationResults.produits?.length || 0} produits...
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Résultats */}
                                        <Row className="g-3 mb-4">
                                            <Col sm={4}>
                                                <div className="kpi-card kpi-success">
                                                    <div className="kpi-icon">
                                                        <i className="ri-check-line fs-24"></i>
                                                    </div>
                                                    <div className="kpi-content">
                                                        <span className="kpi-label">Produits valides</span>
                                                        <h3 className="kpi-value">{validationResults.valid || 0}</h3>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col sm={4}>
                                                <div className="kpi-card kpi-danger">
                                                    <div className="kpi-icon">
                                                        <i className="ri-close-line fs-24"></i>
                                                    </div>
                                                    <div className="kpi-content">
                                                        <span className="kpi-label">Erreurs</span>
                                                        <h3 className="kpi-value">{validationResults.invalid || 0}</h3>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col sm={4}>
                                                <div className="kpi-card kpi-primary">
                                                    <div className="kpi-icon">
                                                        <i className="ri-stack-line fs-24"></i>
                                                    </div>
                                                    <div className="kpi-content">
                                                        <span className="kpi-label">Total lignes</span>
                                                        <h3 className="kpi-value">{filePreview.length - 1 || 0}</h3>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* Aperçu */}
                                        <h6 className="fw-semibold mb-3">Aperçu des données :</h6>
                                        <div className="table-responsive mb-4">
                                            <Table striped bordered size="sm" className="small">
                                                <thead className="bg-light">
                                                    <tr>
                                                        {filePreview[0]?.map((h, i) => (
                                                            <th key={i}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filePreview.slice(1, 6).map((row, i) => (
                                                        <tr key={i}>
                                                            {row.map((cell, j) => (
                                                                <td key={j}>{cell || '-'}</td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>

                                        {/* Erreurs */}
                                        {validationResults.errors?.length > 0 && (
                                            <div className="mb-4">
                                                <h6 className="text-danger fw-semibold mb-2">
                                                    <i className="ri-error-warning-line me-2"></i>
                                                    Erreurs détectées :
                                                </h6>
                                                <div className="error-list p-3 rounded-3" style={{ background: '#fef2f2', maxHeight: '150px', overflowY: 'auto' }}>
                                                    {validationResults.errors.slice(0, 10).map((err, i) => (
                                                        <div key={i} className="small text-danger mb-1">
                                                            <i className="ri-close-circle-line me-1"></i>
                                                            {err}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Actions */}
                                        <div className="d-flex justify-content-between">
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setImportStep(1)}
                                                className="rounded-pill px-4"
                                            >
                                                Retour
                                            </Button>
                                            <Button 
                                                variant="primary" 
                                                onClick={importProduits}
                                                disabled={validationResults.valid === 0 || importing}
                                                className="rounded-pill px-5"
                                                style={{ background: '#21ACCD', border: 'none' }}
                                            >
                                                Confirmer l'import
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Étape 3: Résultats */}
                    {importStep === 3 && (
                        <Card className="border-0 shadow-sm rounded-3">
                            <Card.Header className="py-3" style={{ 
                                background: importStatus === 'success' ? '#10b981' : importStatus === 'partial' ? '#f59e0b' : '#ef4444',
                                borderTopLeftRadius: '12px', 
                                borderTopRightRadius: '12px' 
                            }}>
                                <h5 className="mb-0 text-white">
                                    <i className={`ri-${importStatus === 'success' ? 'checkbox-circle' : importStatus === 'partial' ? 'error-warning' : 'close-circle'}-line me-2`}></i>
                                    {importStatus === 'success' && 'Import réussi !'}
                                    {importStatus === 'partial' && 'Import partiel'}
                                    {importStatus === 'error' && 'Erreur'}
                                </h5>
                            </Card.Header>
                            <Card.Body className="text-center p-4">
                                {importStatus === 'success' && (
                                    <>
                                        <i className="ri-checkbox-circle-line text-success fs-48 mb-3"></i>
                                        <h4 className="fw-bold">Import terminé avec succès</h4>
                                        <p className="text-muted mb-3">{importStats.imported} produits importés</p>
                                    </>
                                )}

                                {importStatus === 'partial' && (
                                    <>
                                        <i className="ri-error-warning-line text-warning fs-48 mb-3"></i>
                                        <h4 className="fw-bold">Import partiel</h4>
                                        <p className="text-muted mb-3">
                                            {importStats.imported} produits importés<br />
                                            {importStats.failed} échecs
                                        </p>
                                        {importStats.errors?.length > 0 && (
                                            <div className="text-start mt-3">
                                                <h6 className="fw-semibold">Détails des erreurs :</h6>
                                                <ul className="text-danger">
                                                    {importStats.errors.slice(0, 3).map((err, i) => (
                                                        <li key={i} className="small">{err}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                )}

                                {importStatus === 'error' && (
                                    <>
                                        <i className="ri-error-warning-line text-danger fs-48 mb-3"></i>
                                        <h4 className="fw-bold">Une erreur est survenue</h4>
                                        <p className="text-muted mb-4">{error || "L'import n'a pas pu être effectué"}</p>
                                    </>
                                )}

                                <div className="d-flex gap-3 justify-content-center mt-4">
                                    <Button 
                                        variant="outline-primary" 
                                        onClick={() => navigate('/stock/produits')}
                                        className="rounded-pill px-4"
                                        style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                                    >
                                        Voir les produits
                                    </Button>
                                    <Button 
                                        variant="primary" 
                                        onClick={() => {
                                            setImportStep(1);
                                            setSelectedFile(null);
                                            setFilePreview([]);
                                            setValidationResults({});
                                            setImportStats({});
                                        }}
                                        className="rounded-pill px-4"
                                        style={{ background: '#21ACCD', border: 'none' }}
                                    >
                                        Nouvel import
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                .steps-container {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                }
                .step-item {
                    text-align: center;
                }
                .step-circle {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: #f1f5f9;
                    color: #64748b;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 0.5rem;
                    font-weight: 600;
                    transition: all 0.3s ease;
                }
                .step-item.active .step-circle {
                    background: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .step-label {
                    font-size: 0.75rem;
                    color: #64748b;
                }
                .step-item.active .step-label {
                    color: #21ACCD;
                    font-weight: 500;
                }
                .step-line {
                    width: 80px;
                    height: 2px;
                    background: #e2e8f0;
                }
                
                .upload-area {
                    border: 2px dashed #e2e8f0;
                    border-radius: 16px;
                    background: #f8fafc;
                    transition: all 0.3s ease;
                }
                .upload-area:hover {
                    border-color: #21ACCD;
                    background: white;
                }
                
                /* KPI Cards mini */
                .kpi-card {
                    background: white;
                    border-radius: 12px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-success { border-left-color: #10b981; }
                .kpi-danger { border-left-color: #ef4444; }
                .kpi-primary { border-left-color: #21ACCD; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                
                .error-list {
                    border-left: 3px solid #ef4444;
                }
                
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 8px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}